import React from 'react';
import { useState, useEffect, useRef } from "react";
import "../styles/PrivacyPolicy.css";

const sections = [
  {
    id: "introduction",
    number: "1.",
    title: "Introduction",
    content: [
      `Eventfork.com (hereinafter referred to as "Eventfork", "we", "us", or "our") is committed to protecting the privacy and security of your personal data. This Privacy Policy describes how we collect, process, use, and safeguard personal data about you in accordance with applicable data protection laws and regulations.`,
      `This policy applies to all users of our platform — including event organizers, attendees, vendors, and visitors to our website. By accessing or using Eventfork.com, you agree to the terms outlined in this Privacy Policy, effective as of January 1, 2025.`,
    ],
  },
  {
    id: "scope",
    number: "2.",
    title: "Scope & Applicability",
    content: [
      `This Privacy Policy applies to all personal data collected through our website, mobile applications, APIs, and any other digital touchpoints operated by Eventfork.com. It covers information collected from users located globally, with additional rights applicable based on your region.`,
      `This policy does not apply to third-party websites, applications, or services linked from our platform. We encourage you to review the privacy notices of any third-party platforms you visit.`,
    ],
  },
  {
    id: "data-collected",
    number: "3.",
    title: "Personal Data We Collect",
    content: [
      `We collect personal data necessary to provide our services effectively. The categories of personal data we may collect include:`,
    ],
    table: {
      headers: ["Category", "Examples", "Purpose"],
      rows: [
        ["Identity Data", "Full name, username, profile photo", "Account creation and identification"],
        ["Contact Data", "Email address, phone number, postal address", "Communication and notifications"],
        ["Event Data", "Events created, attended, bookmarked", "Service delivery and personalisation"],
        ["Financial Data", "Payment card details, billing address", "Processing transactions securely"],
        ["Technical Data", "IP address, browser type, device identifiers", "Security, analytics, and performance"],
        ["Usage Data", "Pages visited, click patterns, session duration", "Platform improvement and analytics"],
        ["Communications", "Support tickets, feedback messages", "Customer service and dispute resolution"],
      ],
    },
    footer: `We collect this information when you register an account, create or attend events, make a payment, contact our support team, or interact with our platform in any way.`,
  },
  {
    id: "lawful-basis",
    number: "4.",
    title: "Lawful Basis for Processing",
    content: [
      `We process your personal data only where we have a lawful basis to do so. The lawful bases we rely upon include:`,
    ],
    bullets: [
      { label: "Contractual Necessity", text: "Processing is necessary for the performance of a contract with you, including providing the services you have requested." },
      { label: "Legitimate Interests", text: "Processing is necessary for our legitimate business interests, such as fraud prevention, network security, and platform improvement, where these are not overridden by your rights." },
      { label: "Legal Obligation", text: "Processing is required to comply with applicable laws and regulatory obligations." },
      { label: "Consent", text: "Where required, we will seek your explicit consent before processing your data for specific purposes such as marketing communications." },
    ],
  },
  {
    id: "use-of-data",
    number: "5.",
    title: "How We Use Your Personal Data",
    content: [
      `We use the personal data we collect for the following purposes:`,
    ],
    bullets: [
      { label: "Service Delivery", text: "To operate, maintain, and improve the Eventfork platform and deliver the services you request." },
      { label: "Account Management", text: "To create and manage your account, authenticate your identity, and personalise your experience." },
      { label: "Transactional Communications", text: "To send booking confirmations, event reminders, payment receipts, and other service-related notifications." },
      { label: "Marketing & Promotions", text: "To send promotional content about events and features, where you have opted in or where we have a legitimate interest." },
      { label: "Analytics & Research", text: "To understand how users interact with the platform, identify trends, diagnose technical issues, and make product decisions." },
      { label: "Security & Compliance", text: "To detect, prevent, and investigate fraudulent activity, abuse, or other violations of our Terms of Service." },
      { label: "Legal Obligations", text: "To comply with applicable laws, regulations, legal proceedings, or enforceable governmental requests." },
    ],
  },
  {
    id: "sharing",
    number: "6.",
    title: "Disclosure & Sharing of Personal Data",
    content: [
      `Eventfork.com does not sell your personal data to third parties under any circumstances. We may share your personal data with the following categories of recipients, strictly for the purposes set out in this Privacy Policy:`,
    ],
    bullets: [
      { label: "Service Providers", text: "Third-party vendors who assist us in operating our platform, including cloud hosting providers, payment processors, email delivery services, and customer support tools. These providers are bound by contractual obligations to process data only on our instructions." },
      { label: "Event Organisers", text: "Where you register for an event, relevant personal data (such as your name and email) may be shared with the event organiser to facilitate your attendance." },
      { label: "Law Enforcement & Regulators", text: "Where required by law, court order, or regulatory authority, we may disclose your personal data to competent authorities." },
      { label: "Business Transfers", text: "In the event of a merger, acquisition, or sale of assets, your personal data may be transferred to a successor entity subject to equivalent privacy protections." },
    ],
    footer: `All third-party recipients are required to maintain appropriate technical and organisational safeguards and are prohibited from using your data for any purpose other than those specified.`,
  },
  {
    id: "international",
    number: "7.",
    title: "International Data Transfers",
    content: [
      `Eventfork.com operates globally and may transfer personal data to countries outside your country of residence, including countries that may not have the same level of data protection as your home jurisdiction.`,
      `Where we transfer personal data internationally, we ensure appropriate safeguards are in place, including standard contractual clauses approved by relevant data protection authorities, binding corporate rules, or transfers to countries deemed to provide adequate protection.`,
    ],
  },
  {
    id: "retention",
    number: "8.",
    title: "Data Retention",
    content: [
      `We retain personal data only for as long as necessary to fulfil the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements.`,
      `To determine the appropriate retention period, we consider the amount, nature, and sensitivity of the personal data; the potential risk of harm from unauthorised use or disclosure; the purposes for which we process the data; and whether we can achieve those purposes through other means, as well as applicable legal requirements.`,
      `Upon expiry of the applicable retention period, personal data will be securely deleted or anonymised so that it can no longer be associated with you.`,
    ],
  },
  {
    id: "security",
    number: "9.",
    title: "Data Security",
    content: [
      `We have implemented appropriate technical and organisational measures to protect personal data against accidental or unlawful destruction, loss, alteration, unauthorised disclosure, or access. These measures include:`,
    ],
    bullets: [
      { label: "Encryption", text: "All data transmitted between your browser and our servers is encrypted using TLS/SSL protocols. Sensitive data at rest is encrypted using industry-standard algorithms." },
      { label: "Access Controls", text: "Access to personal data is restricted on a need-to-know basis. All staff with access to personal data are subject to confidentiality obligations." },
      { label: "Security Audits", text: "We conduct regular security assessments, penetration tests, and vulnerability reviews of our platform and infrastructure." },
      { label: "Incident Response", text: "We maintain a formal data breach response procedure to detect, report, and investigate security incidents in accordance with applicable law." },
    ],
    footer: `While we implement rigorous security controls, no method of transmission over the internet is completely secure. We encourage users to use strong, unique passwords and to contact us immediately if they suspect unauthorised activity.`,
  },
  {
    id: "rights",
    number: "10.",
    title: "Your Rights",
    content: [
      `Subject to applicable law and the nature of your engagement with Eventfork, you may have the following rights in respect of your personal data:`,
    ],
    bullets: [
      { label: "Right of Access", text: "You may request a copy of the personal data we hold about you and information about how it is processed." },
      { label: "Right to Rectification", text: "You may request that we correct inaccurate or incomplete personal data we hold about you." },
      { label: "Right to Erasure", text: "You may request the deletion of your personal data where there is no legitimate reason for us to continue processing it." },
      { label: "Right to Restrict Processing", text: "You may request that we restrict the processing of your personal data in certain circumstances." },
      { label: "Right to Data Portability", text: "You may request to receive your personal data in a structured, commonly used, machine-readable format." },
      { label: "Right to Object", text: "You may object to the processing of your personal data where we rely on legitimate interests as the lawful basis." },
      { label: "Right to Withdraw Consent", text: "Where processing is based on your consent, you may withdraw that consent at any time without affecting the lawfulness of prior processing." },
    ],
    footer: `To exercise any of the above rights, please submit a request to privacy@eventfork.com. We will respond within 30 days. We may require proof of identity before processing your request.`,
  },
  {
    id: "cookies",
    number: "11.",
    title: "Cookies & Tracking Technologies",
    content: [
      `We use cookies and similar tracking technologies (such as web beacons and pixel tags) to collect information about your browsing behaviour on our platform. This information helps us understand usage patterns, improve the platform experience, and deliver relevant content.`,
      `You can control cookie preferences through your browser settings. Please note that disabling certain cookies may affect the functionality of Eventfork.com. For a detailed description of the cookies we use and their purposes, please refer to our Cookie Policy.`,
    ],
  },
  {
    id: "changes",
    number: "12.",
    title: "Changes to This Policy",
    content: [
      `We reserve the right to update or amend this Privacy Policy at any time to reflect changes in our practices, technology, legal requirements, or other factors. Where changes are material, we will notify you by email or by prominent notice on the platform prior to the changes taking effect.`,
      `We encourage you to review this Privacy Policy periodically to stay informed about how we protect your personal data. The date at the top of this document reflects when it was last updated.`,
    ],
  },
  {
    id: "contact",
    number: "13.",
    title: "Contact & Data Protection Officer",
    content: [
      `If you have any questions, concerns, or complaints regarding this Privacy Policy or our data processing practices, please contact our Privacy Team:`,
    ],
    contactBlock: {
      company: "Eventfork Technologies Pvt. Ltd.",
      address: "Pune, Maharashtra, India",
      email: "info@eventfork.com",
      dpo: "info@eventfork.com",
    },
    footer: `If you are not satisfied with our response, you have the right to lodge a complaint with the relevant data protection supervisory authority in your jurisdiction.`,
  },
];

export default function PrivacyPolicy() {
  const [activeId, setActiveId] = useState("introduction");
  const [scrollPct, setScrollPct] = useState(0);
  const sectionRefs = useRef({});

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      setScrollPct((window.scrollY / (doc.scrollHeight - doc.clientHeight)) * 100);

      let current = sections[0].id;
      for (const s of sections) {
        const el = sectionRefs.current[s.id];
        if (el && el.getBoundingClientRect().top <= 120) current = s.id;
      }
      setActiveId(current);
    };
    
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      window.scrollTo({ top: el.offsetTop - 70, behavior: "smooth" });
    }
  };

  return React.createElement(
    "div",
    {
      style: {
        fontFamily: "'Source Sans 3', sans-serif",
        background: "#f7f8fa",
        color: "#1a1a1a",
        minHeight: "100vh"
      }
    },
    // Progress bar
    React.createElement("div", {
      className: "progress-bar",
      style: { width: `${scrollPct}%` }
    }),
    // Header
    React.createElement(
      "div",
      { className: "header-gradient" },
      React.createElement("div", {
        style: {
          position: "absolute",
          right: -60,
          top: -60,
          width: 280,
          height: 280,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.04)",
          pointerEvents: "none"
        }
      }),
      React.createElement("div", {
        style: {
          position: "absolute",
          right: 80,
          bottom: -80,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.03)",
          pointerEvents: "none"
        }
      }),
      React.createElement(
        "div",
        { style: { maxWidth: 1180, margin: "0 auto", padding: "42px 48px 36px" } },
        React.createElement(
          "p",
          {
            style: {
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(12, 11, 11, 0.65)",
              marginBottom: 10,
              fontWeight: 500
            }
          },
          "Legal & Compliance"
        ),
        React.createElement(
          "h1",
          {
            style: {
              fontFamily: "'Source Serif 4', serif",
              fontSize: "clamp(28px, 4vw, 44px)",
              fontWeight: 700,
              color: "#060000ff",
              marginBottom: 12,
              lineHeight: 1.15
            }
          },
          "Privacy Policy"
        ),
        React.createElement(
          "p",
          {
            style: {
              fontSize: 15,
              color: "rgba(6, 0, 0, 0.75)",
              maxWidth: 620,
              lineHeight: 1.7,
              fontWeight: 300
            }
          },
          "Eventfork.com is committed to protecting the privacy and security of personal data. This document sets out how we collect, use, and safeguard your information."
        ),
        React.createElement(
          "div",
          {
            style: {
              marginTop: 20,
              display: "flex",
              gap: 24,
              flexWrap: "wrap",
              alignItems: "center"
            }
          },
          React.createElement(
            "span",
            {
              style: {
                fontSize: 13,
                color: "rgba(3, 3, 3, 0.6)",
                borderLeft: "2px solid rgba(255,255,255,0.3)",
                paddingLeft: 12
              }
            },
            "Effective Date: 1 January 2025"
          ),
          React.createElement(
            "span",
            {
              style: {
                fontSize: 13,
                color: "rgba(1, 1, 1, 0.6)",
                borderLeft: "2px solid rgba(255,255,255,0.3)",
                paddingLeft: 12
              }
            },
            "Last Reviewed: March 2025"
          )
        )
      )
    ),
    // Breadcrumb
    React.createElement(
      "div",
      { className: "breadcrumb" },
      React.createElement(
        "div",
        {
          style: {
            maxWidth: 1180,
            margin: "0 auto",
            padding: "10px 48px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: "#666"
          }
        },
        React.createElement("span", { style: { color: "#0057b8", cursor: "pointer" } }, "Home"),
        React.createElement("span", null, "›"),
        React.createElement("span", { style: { color: "#0057b8", cursor: "pointer" } }, "Legal"),
        React.createElement("span", null, "›"),
        React.createElement("span", { style: { color: "#444" } }, "Privacy Policy")
      )
    ),
    // Body
    React.createElement(
      "div",
      {
        style: {
          maxWidth: 1180,
          margin: "0 auto",
          padding: "36px 48px 80px",
          display: "flex",
          gap: 36,
          alignItems: "flex-start"
        }
      },
      // Sidebar
      React.createElement(
        "aside",
        { style: { width: 240, flexShrink: 0 }, className: "sticky-top" },
        React.createElement(
          "div",
          { className: "toc-sidebar" },
          React.createElement(
            "div",
            { className: "toc-header" },
            React.createElement("p", { className: "toc-header-text" }, "Table of Contents")
          ),
          React.createElement(
            "nav",
            { style: { padding: "8px 0" } },
            sections.map((s) =>
              React.createElement(
                "button",
                {
                  key: s.id,
                  className: `nav-link${activeId === s.id ? " active" : ""}`,
                  onClick: () => scrollTo(s.id)
                },
                React.createElement("span", { style: { color: "#aaa", marginRight: 6, fontSize: 12 } }, s.number),
                s.title
              )
            )
          )
        ),
        React.createElement(
          "div",
          { className: "document-card" },
          React.createElement("p", { style: { fontSize: 12.5, fontWeight: 600, color: "#0d1b2e", marginBottom: 10 } }, "Document Options"),
          React.createElement("button", { className: "btn-outlineee" }, "🖨 Print this page"),
          React.createElement("button", { className: "btn-outlineee" }, "📧 Email this policy")
        )
      ),
      // Main content
      React.createElement(
        "main",
        { style: { flex: 1, minWidth: 0 } },
        React.createElement(
          "div",
          { className: "notice-banner" },
          React.createElement("span", { style: { fontSize: 18, lineHeight: 1.2 } }, "ℹ️"),
          React.createElement(
            "p",
            { style: { fontSize: 14, color: "#5a4000", lineHeight: 1.7 } },
            React.createElement("strong", null, "Important Notice:"),
            " This Privacy Policy was last updated on 1 January 2025. We encourage you to read this document carefully. If you do not agree with these terms, please discontinue use of our platform."
          )
        ),
        sections.map((section, idx) =>
          React.createElement(
            "div",
            {
              key: section.id,
              id: section.id,
              ref: (el) => (sectionRefs.current[section.id] = el),
              className: "section-block appear",
              style: { animationDelay: `${idx * 0.03}s` }
            },
            React.createElement(
              "h2",
              { className: "section-titlee" },
              React.createElement("span", { className: "section-number" }, section.number),
              section.title
            ),
            section.content && section.content.map((para, i) =>
              React.createElement("p", { key: i, className: "prose" }, para)
            ),
            section.table &&
              React.createElement(
                "div",
                { style: { overflowX: "auto", margin: "14px 0" } },
                React.createElement(
                  "table",
                  { className: "data-table" },
                  React.createElement(
                    "thead",
                    null,
                    React.createElement(
                      "tr",
                      null,
                      section.table.headers.map((h) => React.createElement("th", { key: h }, h))
                    )
                  ),
                  React.createElement(
                    "tbody",
                    null,
                    section.table.rows.map((row, ri) =>
                      React.createElement(
                        "tr",
                        { key: ri },
                        row.map((cell, ci) =>
                          React.createElement(
                            "td",
                            {
                              key: ci,
                              style: ci === 0 ? { fontWeight: 600, color: "#0d1b2e", whiteSpace: "nowrap" } : {}
                            },
                            cell
                          )
                        )
                      )
                    )
                  )
                )
              ),
            section.bullets &&
              React.createElement(
                "div",
                { className: "bullet-list" },
                section.bullets.map((b, i) =>
                  React.createElement(
                    "div",
                    { key: i, className: "bullet-item" },
                    React.createElement("span", { className: "bullet-label" }, b.label),
                    React.createElement("span", { className: "bullet-text" }, b.text)
                  )
                )
              ),
            section.footer && React.createElement("p", { className: "footer-note" }, section.footer),
            section.contactBlock &&
              React.createElement(
                "div",
                { className: "contact-box" },
                React.createElement(
                  "div",
                  { className: "contact-row" },
                  React.createElement("span", { className: "contact-label" }, "Company"),
                  React.createElement("span", { className: "contact-value" }, section.contactBlock.company)
                ),
                React.createElement(
                  "div",
                  { className: "contact-row" },
                  React.createElement("span", { className: "contact-label" }, "Registered Address"),
                  React.createElement("span", { className: "contact-value" }, section.contactBlock.address)
                ),
                React.createElement(
                  "div",
                  { className: "contact-row" },
                  React.createElement("span", { className: "contact-label" }, "Privacy Team"),
                  React.createElement(
                    "span",
                    { className: "contact-value" },
                    React.createElement("a", { href: `mailto:${section.contactBlock.email}` }, section.contactBlock.email)
                  )
                ),
                React.createElement(
                  "div",
                  { className: "contact-row" },
                  React.createElement("span", { className: "contact-label" }, "Data Protection Officer"),
                  React.createElement(
                    "span",
                    { className: "contact-value" },
                    React.createElement("a", { href: `mailto:${section.contactBlock.dpo}` }, section.contactBlock.dpo)
                  )
                )
              )
          )
        ),
        
      )
    )
    
  );
  
}